import {
  createContext,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Tunnel } from './types';
import usePreservedCallback from '../react/usePreservedCallback';
import { v4 as uuid } from 'uuid';

const TunnelContext = createContext<Tunnel.Context | null>(null);

export const TunnelProvider = ({ children }: PropsWithChildren) => {
  const [nodeMap, setNodeMap] = useState<Map<string, ReactNode>>(new Map());

  const mount = usePreservedCallback((id: string, node: ReactNode) => {
    setNodeMap((prev) => {
      const map = new Map(prev);
      map.set(id, node);

      return map;
    });
  });

  const unmount = usePreservedCallback((id: string) => {
    setNodeMap((prev) => {
      const map = new Map(prev);
      map.delete(id);

      return map;
    });
  });

  const context = useMemo(() => ({ mount, unmount }), [mount, unmount]);

  return (
    <TunnelContext.Provider value={context}>
      {children}
      {[...nodeMap.entries()].map(([id, element]) => (
        <Fragment key={id}>{element}</Fragment>
      ))}
    </TunnelContext.Provider>
  );
};

export const useTunnel: Tunnel.Out = () => {
  const context = useContext(TunnelContext);

  if (context === null) {
    throw new Error('useTunnel must be used within a Disclosure component');
  }

  const { mount, unmount } = context;

  return usePreservedCallback((register: Tunnel.Register) => {
    const id = uuid();
    const node = register({ close: () => unmount(id) });

    mount(id, node);
  });
};
